import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import {
  createColumn,
  getForm,
  getRegister,
  formatDateTime,
  createEditAction,
  addSelectItem,
  capitalize,
} from "../../utils/helpers";
import { URL_BOOKING_RESERVATION_RATES } from "../../utils/urls";
import useGet from "../common/useGet";
import {
  API_CONFIGURABLES,
  API_CONFIGURABLES_BOOKING,
  API_DISTRICTS,
  API_PARKINGS,
  API_STICKER_TYPES,
  API_STREETS,
} from "../../utils/apis";
import {
  BOOKING_RESERVATION_RATE,
  DAILY,
  MONTHLY,
  RESERVATION,
  RESERVATION_TYPE,
  STICKER,
  STICKER_TYPE,
  WEEKLY,
} from "../../utils/constants";
import { IFormSection, TFormHookParams } from "../../types/common";

// Helper: Resolve City Name by ID
const resolveCityName = (cityId, citiesData) => {
  if (!cityId || !citiesData) return "Unknown City";
  const citiesArray = Array.isArray(citiesData)
    ? citiesData
    : citiesData?.results || [];
  if (!citiesArray.length) return "Loading...";
  const city = citiesArray.find((c) => c.id === cityId);
  return city ? city.name : "Unknown City";
};

// Helper: Resolve Parking Name by ID
const resolveParkingName = (parkingId, parkingsData, bookingType) => {
  // If booking type is sticker and no parking ID, return appropriate message
  if (bookingType === "sticker" && !parkingId) {
    return "N/A"; // or "Not Applicable" or "-"
  }

  if (!parkingId || !parkingsData) return "Unknown Parking";

  const parkingsArray = Array.isArray(parkingsData)
    ? parkingsData
    : parkingsData?.results || [];

  if (!parkingsArray.length) return "Loading...";

  const parking = parkingsArray.find((p) => p.id === parkingId);
  return parking ? parking.name : "Unknown Parking";
};

export const useBookingReservationRates = () => {
  // Get cities, streets, and parkings data for resolving names
  const {
    loadData: loadCities,
    loading: loadingCities,
    data: citiesData,
  } = useGet();

  const {
    loadData: loadStreets,
    loading: loadingStreets,
    data: streetsData,
  } = useGet();

  const {
    loadData: loadStickerType,
    loading: loadingStickerTypes,
    data: stickerTypesData,
  } = useGet();

  const {
    loadData: loadReservationType,
    loading: loadingReservationTypes,
    data: reservationTypesData,
  } = useGet();

  const {
    loadData: loadParkingData,
    loading: loadingParkings,
    data: parkingsData,
  } = useGet();

  useEffect(() => {
    loadCities({ api: API_DISTRICTS });
    loadStreets({ api: API_STREETS });
    loadParkingData({ api: API_PARKINGS });

    loadStickerType({
      api: API_STICKER_TYPES,
      // params: {
      //   key: "city",
      // },
    });

    loadReservationType({
      api: API_CONFIGURABLES,
      params: {
        key: RESERVATION_TYPE,
      },
    });
  }, []);

  const tableColumns = () => [
    createColumn("booking_type", true),
    createColumn("type_name", true),
    createColumn("amount", true),
    createColumn("city", true),
    createColumn("parking", true),
    createColumn("status", false),
    createColumn("actions", false),
  ];

  const tableData = (data) => {
    if (!data) return [];

    return data.map((item) => ({
      ...item,
      booking_type: capitalize(item.booking_type),
      city: resolveCityName(item?.city, citiesData),
      // street: resolveStreetName(item?.street, streetsData),
      parking: resolveParkingName(
        item?.parking,
        parkingsData,
        item?.booking_type,
      ),
      status: (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            !item.deleted
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {!item.deleted ? "Active" : "Deleted"}
        </span>
      ),
      created_at: formatDateTime(item.created_at),
      updated_at: formatDateTime(item.updated_at),
      actions: (
        <FormActions
          actions={[
            createEditAction(URL_BOOKING_RESERVATION_RATES, item?.id),
            // createDeleteAction(URL_BOOKING_RESERVATION_RATES, item?.id),
          ]}
        />
      ),
    }));
  };

  const formRoute = getForm(URL_BOOKING_RESERVATION_RATES);

  return {
    tableData,
    tableColumns,
    formRoute,
    citiesData,
    streetsData,
    parkingsData,
    loadingCities,
    loadingStreets,
    loadingParkings,
    loadingReservationTypes,
    loadingStickerTypes,
    reservationTypesData,
    stickerTypesData,
  };
};

export const useBookingReservationRateForm = ({
  form,
  updateFormValues,
}: TFormHookParams = {}) => {
  const {
    loadData: loadCities,
    loading: loadingCities,
    data: cities,
  } = useGet();

  const {
    loadData: loadParkingsUnderStreet,
    loading: loadingParkingsUnderStreet,
    data: parkingsUnderStreet,
  } = useGet();

  const {
    loadData: loadStreets,
    loading: loadingStreets,
    data: streetsData,
  } = useGet();

  const {
    loadData: loadStickerType,
    loading: loadingStickerTypes,
    data: stickerTypesData,
  } = useGet();

  const {
    loadData: loadReservationType,
    loading: loadingReservationTypes,
    data: reservationTypesData,
  } = useGet();

  const { city, street, booking_type } = form || {};

  // Load cities on component mount
  useEffect(() => {
    loadCities({ api: API_DISTRICTS });
  }, []);

  // Load streets when city changes
  useEffect(() => {
    if (city) {
      loadStreets({ api: API_STREETS, params: { city: city } });
    }
  }, [city]);

  // Load reservation types when booking_type changes to 'reservation'
  useEffect(() => {
    if (booking_type === RESERVATION) {
      loadReservationType({
        api: API_CONFIGURABLES_BOOKING,
        params: {
          key: RESERVATION_TYPE,
        },
      });
    }
  }, [booking_type]);

  // Load sticker types when booking_type changes to 'sticker'
  useEffect(() => {
    if (booking_type === STICKER) {
      loadStickerType({
        api: API_CONFIGURABLES_BOOKING,
        params: {
          city: city,
          key: STICKER_TYPE,
        },
      });
    }
  }, [booking_type, city]);

  // Load parkings when street changes
  useEffect(() => {
    if (street) {
      loadParkingsUnderStreet({
        api: API_PARKINGS,
        params: { street: street },
      });
    }
  }, [street]);

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues({
        street_type: BOOKING_RESERVATION_RATE,
      });
    }
  }, [updateFormValues]);

  const sections: IFormSection[] = [
    {
      title: "Rate Configuration",
      fields: [
        {
          field_name: "booking_type",
          label: "Booking Type",
          type: "select",
          props: { required: true },
          options_data: [
            addSelectItem({ label: "Reservation", value: "reservation" }),
            addSelectItem({ label: "Sticker", value: "sticker" }),
            // addSelectItem({ label: "Advance", value: "advance" }),
          ],
        },
        {
          field_name: "city",
          type: "select",
          label: "City",
          props: { required: true },
          options_data: cities?.results || cities,
          loading: loadingCities,
        },
        {
          field_name: "street",
          type: "select",
          label: "Street",
          props: { required: form?.booking_type == STICKER ? false : true },
          options_data: streetsData?.results || streetsData,
          loading: loadingStreets,
          visible: form?.booking_type === RESERVATION,
        },
        {
          field_name: "parking",
          type: "select",
          label: "Parking Yard",
          props: { required: true },
          options_data: parkingsUnderStreet?.results || parkingsUnderStreet,
          loading: loadingParkingsUnderStreet,
          visible: form?.booking_type === RESERVATION,
          // Disable parking dropdown until street is selected
        },

        {
          field_name: "bk_type",
          type: "select",
          label: "Reservation Type",
          props: { required: true },
          options_data: reservationTypesData?.results || reservationTypesData,
          loading: loadingReservationTypes,
          visible: form?.booking_type === RESERVATION,
        },

        {
          field_name: "bk_type",
          type: "select",
          label: "Sticker Type",
          props: { required: true },
          options_data: stickerTypesData?.results || stickerTypesData,
          loading: loadingStickerTypes,
          visible: form?.booking_type === STICKER,
        },

        {
          field_name: "amount",
          label: "Amount",
          type: "text",
          props: {
            required: true,
            min: 0,
            step: 0.01,
            placeholder: "Enter amount",
          },
        },
        {
          field_name: "duration_type",
          type: "select",
          label: "Duration Type",
          props: { required: true },
          options_data: [
            { value: DAILY, description: "Days" },
            { value: WEEKLY, description: "Weeks" },
            { value: MONTHLY, description: "Months" },
          ].map((data) => addSelectItem(data)),
          visible: form?.booking_type === STICKER,
        },
      ],
    },
  ];

  const title = "Booking Reservation Rate Management";
  const redirect = getRegister(URL_BOOKING_RESERVATION_RATES);

  const defaultValues = {
    // booking_type: "reservation",
    // is_organization_rate: false,
    // deleted: false,
    // rate_per_slot: 1000,
    // rate_per_day: 5000,
    // city: "f1dfd788-94ab-4574-bdc6-4b3bb222c234",
    // parking: "cd98a009-1dba-4872-bf3e-0efb02d91e38",
    // rate_name: "Standard Reservation Rate",
  };

  return {
    sections,
    title,
    redirect,
    loading:
      loadingCities ||
      loadingParkingsUnderStreet ||
      loadingStreets ||
      loadingReservationTypes ||
      loadingStickerTypes,
    defaultValues,
  };
};

export const BookingReservationRateForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}) => {
  const { sections } = useBookingReservationRateForm({
    form,
    updateFormValues: updateFormValue,
  });

  return (
    <div>
      {sections.map((section: IFormSection, index: number) => (
        <div key={index}>
          <h3>{section.title}</h3>
          <FormFields
            form={form}
            error={error}
            updateFormValue={updateFormValue}
            onChange={onChange}
            fields={section.fields}
          />
        </div>
      ))}
    </div>
  );
};
